// Platform operating-cost ledger. Each row is one price for one vendor
// product over a date span. A rate change ends the old row and inserts
// the next one so later profitability math can see what was in effect.

export const PLATFORM_COST_CATEGORIES = [
  "email",
  "hosting",
  "database",
  "ai",
  "storage",
  "auth",
  "domain",
  "observability",
  "other",
] as const;

export type PlatformCostCategory = (typeof PLATFORM_COST_CATEGORIES)[number];

export const PLATFORM_COST_CATEGORY_LABELS: Record<PlatformCostCategory, string> = {
  email: "Email",
  hosting: "Hosting",
  database: "Database",
  ai: "AI",
  storage: "Storage",
  auth: "Auth",
  domain: "Domain",
  observability: "Observability",
  other: "Other",
};

export const PLATFORM_COST_CADENCES = ["monthly", "annual", "one_time"] as const;

export type PlatformCostCadence = (typeof PLATFORM_COST_CADENCES)[number];

export const PLATFORM_COST_CADENCE_LABELS: Record<PlatformCostCadence, string> = {
  monthly: "Monthly",
  annual: "Annual",
  one_time: "One-time",
};

/** $1,000,000 per row. Stops a dropped digit from landing as a real bill. */
export const PLATFORM_COST_MAX_CENTS = 100_000_000;

const ISO_DATE = /^\d{4}-\d{2}-\d{2}$/;

export type PlatformCostLedgerRow = {
  id: string;
  vendor: string;
  product_name: string;
  category: PlatformCostCategory;
  amount_cents: number;
  currency: "USD";
  cadence: PlatformCostCadence;
  started_on: string;
  ended_on: string | null;
  notes: string | null;
  supersedes_id: string | null;
  created_at: string;
  updated_at: string;
};

export type PlatformCostDraft = {
  vendor: string;
  product_name: string;
  category: PlatformCostCategory;
  amount_cents: number;
  cadence: PlatformCostCadence;
  started_on: string;
  ended_on: string | null;
  notes: string | null;
};

export type PlatformCostSummary = {
  /** Active monthly plans plus one-twelfth of active annual plans. */
  monthlyCents: number;
  /** Exact annualized total in cents (monthly × 12, annual as billed). */
  annualizedCents: number;
  activeCount: number;
  scheduledCount: number;
};

export function isPlatformCostCategory(value: string): value is PlatformCostCategory {
  return (PLATFORM_COST_CATEGORIES as readonly string[]).includes(value);
}

export function isPlatformCostCadence(value: string): value is PlatformCostCadence {
  return (PLATFORM_COST_CADENCES as readonly string[]).includes(value);
}

export function isIsoDate(value: string): boolean {
  if (!ISO_DATE.test(value)) return false;
  const [y, m, d] = value.split("-").map(Number);
  const dt = new Date(Date.UTC(y, m - 1, d));
  return dt.getUTCFullYear() === y && dt.getUTCMonth() === m - 1 && dt.getUTCDate() === d;
}

export function shiftIsoDate(iso: string, days: number): string {
  const [y, m, d] = iso.split("-").map(Number);
  const dt = new Date(Date.UTC(y, m - 1, d + days));
  const yy = dt.getUTCFullYear();
  const mm = String(dt.getUTCMonth() + 1).padStart(2, "0");
  const dd = String(dt.getUTCDate()).padStart(2, "0");
  return `${yy}-${mm}-${dd}`;
}

export function localTodayIso(now = new Date()): string {
  const y = now.getFullYear();
  const m = String(now.getMonth() + 1).padStart(2, "0");
  const d = String(now.getDate()).padStart(2, "0");
  return `${y}-${m}-${d}`;
}

export function parseDollarsToCents(raw: string): number | null {
  const s = raw.trim().replace(/[$,]/g, "");
  if (!/^\d+(\.\d{1,2})?$/.test(s)) return null;
  const [whole, frac = ""] = s.split(".");
  const cents = Number(whole) * 100 + Number(frac.padEnd(2, "0"));
  if (!Number.isSafeInteger(cents) || cents > PLATFORM_COST_MAX_CENTS) return null;
  return cents;
}

export function centsToDollarInput(cents: number): string {
  return (cents / 100).toFixed(2);
}

export function formatUsdFromCents(cents: number): string {
  return new Intl.NumberFormat("en-US", { style: "currency", currency: "USD" }).format(cents / 100);
}

const MONTHS = ["Jan", "Feb", "Mar", "Apr", "May", "Jun", "Jul", "Aug", "Sep", "Oct", "Nov", "Dec"];

export function formatIsoDate(iso: string): string {
  const [y, m, d] = iso.slice(0, 10).split("-");
  const mi = Number(m) - 1;
  if (!y || !m || !d || mi < 0 || mi > 11) return iso;
  return `${MONTHS[mi]} ${Number(d)}, ${y}`;
}

export function annualizedCents(row: { cadence: PlatformCostCadence; amount_cents: number }): number {
  if (row.cadence === "monthly") return row.amount_cents * 12;
  if (row.cadence === "annual") return row.amount_cents;
  return 0;
}

/** Recurring row whose span covers asOf. One-time charges are never in force. */
export function isInForce(
  row: { cadence: PlatformCostCadence; started_on: string; ended_on: string | null },
  asOf: string,
): boolean {
  if (row.cadence === "one_time") return false;
  if (row.started_on > asOf) return false;
  if (row.ended_on != null && row.ended_on < asOf) return false;
  return true;
}

export function summarizePlatformCosts(rows: PlatformCostLedgerRow[], asOf: string): PlatformCostSummary {
  let annualizedCentsTotal = 0;
  let activeCount = 0;
  let scheduledCount = 0;
  for (const row of rows) {
    if (row.cadence === "one_time") continue;
    if (isInForce(row, asOf)) {
      annualizedCentsTotal += annualizedCents(row);
      activeCount += 1;
    } else if (row.started_on > asOf && (row.ended_on == null || row.ended_on >= asOf)) {
      scheduledCount += 1;
    }
  }
  return {
    monthlyCents: annualizedCentsTotal / 12,
    annualizedCents: annualizedCentsTotal,
    activeCount,
    scheduledCount,
  };
}

export function groupPlatformCosts(rows: PlatformCostLedgerRow[], asOf: string) {
  const active: PlatformCostLedgerRow[] = [];
  const scheduled: PlatformCostLedgerRow[] = [];
  const oneTime: PlatformCostLedgerRow[] = [];
  const ended: PlatformCostLedgerRow[] = [];
  for (const row of rows) {
    if (row.cadence === "one_time") oneTime.push(row);
    else if (row.started_on > asOf && (row.ended_on == null || row.ended_on >= asOf)) scheduled.push(row);
    else if (isInForce(row, asOf)) active.push(row);
    else ended.push(row);
  }
  const byStartDesc = (a: PlatformCostLedgerRow, b: PlatformCostLedgerRow) =>
    b.started_on.localeCompare(a.started_on) || a.vendor.localeCompare(b.vendor);
  active.sort(byStartDesc);
  scheduled.sort(byStartDesc);
  oneTime.sort(byStartDesc);
  ended.sort(byStartDesc);
  return { active, scheduled, oneTime, ended };
}

export function normalizePlatformCostDraft(
  input: PlatformCostDraft,
): { ok: true; draft: PlatformCostDraft } | { ok: false; error: string } {
  const vendor = input.vendor.trim();
  const productName = input.product_name.trim();
  const notesRaw = input.notes?.trim() ?? "";
  if (!vendor || vendor.length > 120) return { ok: false, error: "Vendor is required (120 characters max)." };
  if (!productName || productName.length > 120) {
    return { ok: false, error: "Product is required (120 characters max)." };
  }
  if (!isPlatformCostCategory(input.category)) return { ok: false, error: "Pick a category." };
  if (!isPlatformCostCadence(input.cadence)) return { ok: false, error: "Pick a cadence." };
  if (!Number.isInteger(input.amount_cents) || input.amount_cents < 0 || input.amount_cents > PLATFORM_COST_MAX_CENTS) {
    return { ok: false, error: "Amount must be from $0.00 to $1,000,000.00." };
  }
  if (!isIsoDate(input.started_on)) return { ok: false, error: "Start date must be a real calendar date." };
  if (input.ended_on != null && input.ended_on !== "") {
    if (!isIsoDate(input.ended_on)) return { ok: false, error: "End date must be a real calendar date." };
    if (input.ended_on < input.started_on) return { ok: false, error: "End date has to fall on or after the start date." };
  }
  if (notesRaw.length > 2000) return { ok: false, error: "Notes are limited to 2000 characters." };
  return {
    ok: true,
    draft: {
      vendor,
      product_name: productName,
      category: input.category,
      amount_cents: input.amount_cents,
      cadence: input.cadence,
      started_on: input.started_on,
      ended_on: input.ended_on ? input.ended_on : null,
      notes: notesRaw ? notesRaw : null,
    },
  };
}

export function validateRateChange(
  predecessor: { cadence: PlatformCostCadence; started_on: string; ended_on: string | null },
  nextStartedOn: string,
): { ok: true; predecessorEndedOn: string } | { ok: false; error: string } {
  if (predecessor.cadence === "one_time") {
    return { ok: false, error: "One-time charges do not have a follow-on rate. Add another charge instead." };
  }
  if (predecessor.ended_on) {
    return { ok: false, error: "This rate is already ended. Add a new cost instead." };
  }
  if (!isIsoDate(nextStartedOn)) return { ok: false, error: "Start date must be a real calendar date." };
  if (nextStartedOn <= predecessor.started_on) {
    return { ok: false, error: "The new rate has to start after the current one. Use Edit to correct this row." };
  }
  return { ok: true, predecessorEndedOn: shiftIsoDate(nextStartedOn, -1) };
}
